/**
 * React Query hooks for feed purchase events.
 * React Query hooks for the corresponding entity.
 */

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "common/queryKeys";
import { API } from "common/utils";
import { fetchApi } from "common/utils/Api";
import { useGuardedMutation } from "model/useGuardedMutation";
import {
  FEED_PURCHASE_EVENT_REJECTION_PERIOD_DAYS,
  GeneralFeedPurchase,
  GeneralFeedPurchaseEvent,
} from "./feedPurchaseEventTypes";
import { pollInsightsRefresh } from "./useInsightsQueries";

// Re-export types for convenience
export { FEED_PURCHASE_EVENT_REJECTION_PERIOD_DAYS, feedPurchaseApi };
export type { GeneralFeedPurchase, GeneralFeedPurchaseEvent };

class FeedPurchaseAPI extends API<GeneralFeedPurchaseEvent> {
  async getAllFeedPurchaseEventsForFarm(
    farmGuid: string,
    feedPurchaseEventTimeAfter?: string
  ): Promise<GeneralFeedPurchaseEvent[]> {
    const feedPurchaseEventTimeAfterParam = feedPurchaseEventTimeAfter
      ? `&feedPurchaseEventTimeAfter=${feedPurchaseEventTimeAfter}`
      : "";
    const res = await fetch(
      `${this.baseUrl}${this.entity}?farmGuid=${farmGuid}${feedPurchaseEventTimeAfterParam}`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${window.localStorage.getItem("jwt") || ""}`,
          "Content-Type": "application/json",
        },
      }
    );
    return (await res.json()) as GeneralFeedPurchaseEvent[];
  }
}

const feedPurchaseApi = new FeedPurchaseAPI("feedPurchase");

// Event data changes infrequently after creation - use 5 minute stale time
// to reduce unnecessary refetches while keeping data reasonably fresh
const EVENT_STALE_TIME = 5 * 60 * 1000; // 5 minutes

/**
 * Fetch all feed purchase events for a farm
 */
export function useFeedPurchaseEvents(
  farmGuid: string | undefined,
  feedPurchaseEventTimeAfter?: string
) {
  return useQuery({
    queryKey: [
      ...queryKeys.events.feedPurchase.all,
      farmGuid,
      feedPurchaseEventTimeAfter,
    ],
    queryFn: () =>
      feedPurchaseApi.getAllFeedPurchaseEventsForFarm(
        farmGuid ?? "",
        feedPurchaseEventTimeAfter
      ),
    enabled: !!farmGuid,
    staleTime: EVENT_STALE_TIME,
  });
}

/**
 * Fetch a single feed purchase event by guid
 */
export function useFeedPurchaseEvent(guid: string | undefined) {
  return useQuery({
    queryKey: ["feedPurchaseEvent", guid],
    queryFn: () => feedPurchaseApi.getOne(guid ?? ""),
    enabled: !!guid,
    staleTime: EVENT_STALE_TIME,
  });
}

/**
 * Create a new feed purchase event
 */
export function useCreateFeedPurchaseEvent() {
  const queryClient = useQueryClient();

  return useGuardedMutation({
    mutationFn: async (generalFeedPurchaseEvent: GeneralFeedPurchaseEvent) => {
      const result = await feedPurchaseApi.createOne(generalFeedPurchaseEvent);
      return { ...generalFeedPurchaseEvent, ...result };
    },
    onSuccess: async (_, feedPurchaseEvent) => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.events.feedPurchase.all,
      });
      // Also invalidate feed stock since feed purchase events affect it
      await queryClient.invalidateQueries({
        queryKey: queryKeys.feedStock.byFarm(feedPurchaseEvent.farmGuid),
      });
      pollInsightsRefresh(queryClient, ["feedInStore_"]);
    },
  });
}

/**
 * Update an existing feed purchase event
 */
export function useUpdateFeedPurchaseEvent() {
  const queryClient = useQueryClient();

  return useGuardedMutation({
    mutationFn: async (generalFeedPurchaseEvent: GeneralFeedPurchaseEvent) => {
      const result = await feedPurchaseApi.updateOne(generalFeedPurchaseEvent);
      return { ...generalFeedPurchaseEvent, ...result };
    },
    onSuccess: async (updatedEvent) => {
      queryClient.setQueryData(
        ["feedPurchaseEvent", updatedEvent.guid],
        updatedEvent
      );
      await queryClient.invalidateQueries({
        queryKey: queryKeys.events.feedPurchase.all,
      });
      // Also invalidate feed stock
      await queryClient.invalidateQueries({
        queryKey: queryKeys.feedStock.byFarm(updatedEvent.farmGuid),
      });
      pollInsightsRefresh(queryClient, ["feedInStore_"]);
    },
  });
}

/**
 * Delete a feed purchase event
 */
export function useDeleteFeedPurchaseEvent() {
  const queryClient = useQueryClient();

  return useGuardedMutation({
    mutationFn: async (guid: string) => {
      await fetchApi(`feedPurchase/${guid}`, { method: "DELETE" });
      return guid;
    },
    onSuccess: async (guid) => {
      queryClient.removeQueries({ queryKey: ["feedPurchaseEvent", guid] });
      await queryClient.invalidateQueries({
        queryKey: queryKeys.events.feedPurchase.all,
      });
      pollInsightsRefresh(queryClient, ["feedInStore_"]);
    },
  });
}
